import { Schemas } from '~/clients/api-client'
import { DepartmentKey } from '~/store/slices/filterSlice'
import { useStore } from '~/store/store'
import { Department, usePractitionerStatusDefinition } from '~/storm'

export type PractitionerAssignment = Schemas['PractitionerAssignment']
export type PractitionerStatusDefinition = Schemas['PractitionerStatusDefinitionOut']
export type TimeOfDay = Schemas['TimeOfDay']

export const assignmentToLabel: Record<string, string> = {
    surgery: 'Operasjon',
    policlinic: 'Poliklinikk',
    absence: 'Utilgjengelig',
    on_call_surgery: 'Annet',
} as const

const UNKNOWN_LABEL = 'Utilgjengelig'

export const isAssignment = (assignment: string): assignment is PractitionerAssignment => assignment in assignmentToLabel
export const mapAssignmentToLabel = (assignment: string) => (isAssignment(assignment) ? (assignmentToLabel[assignment] ?? UNKNOWN_LABEL) : UNKNOWN_LABEL)

/**
 * Stable ordering for assignments: surgery (0) -> policlinic (1) -> null/unknown (2) -> on_call_surgery (3)
 */
const assignmentOrder: Record<string, number> = {
    surgery: 0,
    policlinic: 1,
    absence: 2,
    on_call_surgery: 3,
}

export const getAssignmentOrder = (assignment: PractitionerAssignment | null | undefined): number => (assignment ? (assignmentOrder[assignment] ?? 2) : 2)

/**
 * Stable ordering for time of day: day_time/null/unknown (1) -> evening (2)
 * Earlier in the day goes first (left side), later in the day goes last (right side).
 */
const timeOfDayOrder: Record<TimeOfDay, number> = {
    day_time: 1,
    evening: 2,
}

export const getTimeOfDayOrder = (timeOfDay: TimeOfDay | null | undefined): number => (timeOfDay ? timeOfDayOrder[timeOfDay] : 1)

/**
 * Sort status definitions by assignment order, then by the `order` property.
 */
export function sortStatusDefinitions<T extends Pick<PractitionerStatusDefinition, 'assignment' | 'order'>>(definitions: T[]): T[] {
    return definitions.toSorted((a, b) => {
        const orderA = getAssignmentOrder(a.assignment)
        const orderB = getAssignmentOrder(b.assignment)
        if (orderA !== orderB) return orderA - orderB
        return a.order - b.order
    })
}

type GroupedStatusDefinition = {
    assignment: PractitionerAssignment
    title: string
    statusCodes: string[]
}

/**
 * Group status definitions by their assignment, label each group, and sort groups + items within.
 */
export function groupStatusDefinitionsByAssignment(statusDefinitions: PractitionerStatusDefinition[]): GroupedStatusDefinition[] {
    const grouped = Object.groupBy(statusDefinitions, definition => definition.assignment)

    return Object.entries(grouped)
        .map(([assignment, definitions]) => ({
            assignment: assignment as PractitionerAssignment,
            title: mapAssignmentToLabel(assignment),
            statusCodes: definitions.toSorted((a, b) => a.order - b.order).map(d => d.code),
        }))
        .toSorted((a, b) => getAssignmentOrder(a.assignment) - getAssignmentOrder(b.assignment))
}

export function isDepartmentIncluded(departmentKey: DepartmentKey, departments: readonly Department[]) {
    return departmentKey === 'all' || departments.some(d => d.id === Number(departmentKey))
}

export function useIsStatusPartOfDepartment(status: string): boolean {
    const departmentKey = useStore(state => state.appFilters.departmentKey)
    const definition = usePractitionerStatusDefinition({ where: { code: status }, include: { departments: true } })
    return isDepartmentIncluded(departmentKey, definition?.departments ?? [])
}
